import * as z from 'zod'

import { UpdateConfigsVars } from '@/common/api/params/systemConfig'
import { NameLabelMapping } from '@/common/app/message'
import { extractSystemConfigValue } from '@/common/app/modules/systemConfig'
import { StringLength } from '@/common/form/schema'
import {
  AI_MODEL_CONFIG_KEY_PREFIX,
  AiModelCatalog,
  AiModelTier,
  SystemConfig,
  SystemConfigKey,
  aiModelConfigKey
} from '@/common/model/systemConfig'

export const systemConfigSchema = z.object({
  smtp_host: z.string().max(StringLength.NORMAL_MAX),
  smtp_port: z.string().max(StringLength.NORMAL_MAX),
  smtp_user: z.string().max(StringLength.NORMAL_MAX),
  notify_to: z.string().max(StringLength.NORMAL_MAX),
  smtp_pass: z.string().max(StringLength.NORMAL_MAX),
  ftp_host: z.string().max(StringLength.NORMAL_MAX),
  public_base_url: z.url().max(StringLength.NORMAL_MAX),
  pa_embedding_provider: z.string().max(StringLength.NORMAL_MAX),
  // UGCレビュー依頼メールの分散送信（W2サーバは10万通/時の能力があるため、
  // これはサーバ保護ではなく礼儀と分散のための値）
  ugc_mail_batch_limit: z.string().regex(/^\d*$/, '数値で入力してください'),
  ugc_mail_interval_minutes: z.string().regex(/^\d*$/, '数値で入力してください'),
  ugc_mail_window_start: z.string().regex(/^\d*$/, '数値で入力してください'),
  ugc_mail_window_end: z.string().regex(/^\d*$/, '数値で入力してください'),
  // プロダクトごとの AI モデル（SMART_HUB_DEV-159）。{ [プロダクト]: { standard, light } }。
  // 空文字は「既定のモデルを使う」。キーのドット（ai_model.x.y）はフォームのパスと衝突するため、
  // フォームの中では入れ子で持ち、保存時に ai_model.<プロダクト>.<枠> へ平らにする
  ai_model: z.record(
    z.string(),
    z.object({ standard: z.string().optional(), light: z.string().optional() })
  )
})

export type SystemConfigFormSchemaType = z.infer<typeof systemConfigSchema>

/** 保存済みの AI モデル設定をフォームの形にする。選べないモデル（廃止したもの等）は既定（空）に戻す */
const convertAiModelsToSchema = (
  configs: SystemConfig[],
  catalog?: AiModelCatalog
): SystemConfigFormSchemaType['ai_model'] => {
  if (!catalog) return {}
  const tiers: AiModelTier[] = [AiModelTier.STANDARD, AiModelTier.LIGHT]
  return Object.fromEntries(
    catalog.products.map((product) => [
      product.key,
      Object.fromEntries(
        tiers
          .filter((tier) => product.uses[tier] !== undefined)
          .map((tier) => {
            const saved = configs.find((c) => c.key === aiModelConfigKey(product.key, tier))?.value
            const allowed = catalog.models.some((m) => m.id === saved && m.tiers.includes(tier))
            return [tier, allowed && saved ? saved : '']
          })
      )
    ])
  )
}

export const convertSystemConfigToSchema = (
  configs: SystemConfig[],
  aiModelCatalog?: AiModelCatalog
): SystemConfigFormSchemaType => {
  return {
    smtp_host: extractSystemConfigValue(configs, SystemConfigKey.SMTP_HOST) ?? '',
    smtp_port: extractSystemConfigValue(configs, SystemConfigKey.SMTP_PORT) ?? '',
    smtp_user: extractSystemConfigValue(configs, SystemConfigKey.SMTP_USER) ?? '',
    notify_to: extractSystemConfigValue(configs, SystemConfigKey.NOTIFY_TO) ?? '',
    smtp_pass: extractSystemConfigValue(configs, SystemConfigKey.SMTP_PASS) ?? '',
    ftp_host: extractSystemConfigValue(configs, SystemConfigKey.FTP_HOST) ?? '',
    public_base_url: extractSystemConfigValue(configs, SystemConfigKey.PUBLIC_BASE_URL) ?? '',
    pa_embedding_provider:
      extractSystemConfigValue(configs, SystemConfigKey.PA_EMBEDDING_PROVIDER) ?? 'stub',
    ugc_mail_batch_limit:
      extractSystemConfigValue(configs, SystemConfigKey.UGC_MAIL_BATCH_LIMIT) ?? '200',
    ugc_mail_interval_minutes:
      extractSystemConfigValue(configs, SystemConfigKey.UGC_MAIL_INTERVAL_MINUTES) ?? '15',
    ugc_mail_window_start:
      extractSystemConfigValue(configs, SystemConfigKey.UGC_MAIL_WINDOW_START) ?? '8',
    ugc_mail_window_end:
      extractSystemConfigValue(configs, SystemConfigKey.UGC_MAIL_WINDOW_END) ?? '21',
    ai_model: convertAiModelsToSchema(configs, aiModelCatalog)
  }
}

export const convertSystemConfigSchemaToVars = (
  data: SystemConfigFormSchemaType
): UpdateConfigsVars => {
  const { ai_model: aiModel, ...rest } = data
  // AI モデルは ai_model.<プロダクト>.<枠> のキーに平らにして保存する（空文字＝既定に戻す）
  const aiModelConfigs = Object.entries(aiModel).flatMap(([product, tiers]) =>
    Object.entries(tiers)
      .filter((entry): entry is [string, string] => entry[1] !== undefined)
      .map(([tier, value]) => ({
        key: `${AI_MODEL_CONFIG_KEY_PREFIX}${product}.${tier}`,
        value
      }))
  )
  return {
    configs: [
      ...Object.entries(rest).map(([key, value]) => ({ key, value })),
      ...aiModelConfigs
    ]
  }
}

export const systemConfigNameLabelMapping: NameLabelMapping = {
  smtp_host: 'SMTPサーバー',
  smtp_port: 'SMTPポート',
  smtp_user: '送信元メールアドレス（From）',
  notify_to: '通知先メールアドレス（To）',
  smtp_pass: 'SMTPパスワード',
  ftp_host: 'FTPサーバーIP',
  public_base_url: '公開ベースURL',
  pa_embedding_provider: 'AI購買支援 埋め込みプロバイダ',
  ugc_mail_batch_limit: 'UGCメール 1回の送信件数（サイトごと）',
  ugc_mail_interval_minutes: 'UGCメール 送信頻度（分間隔）',
  ugc_mail_window_start: 'UGCメール 送信開始時刻（時・JST）',
  ugc_mail_window_end: 'UGCメール 送信終了時刻（時・JST）',
  ai_model: 'AIモデル'
}
